import React from "react";
import PropTypes from "prop-types";
import MCard from "components/Shared/MCard.tsx";
import { DATA_TYPE_HYPERLINK } from "typeConstants/dataTypes";
import { openGoogleMapsLink } from "components/Shared/Functions";
import { getPublicPropertyRecord as getPublicPropertyRecordRequest } from "requests/homeowner/ho";
import { createRequestCoalescer } from "tools/requestCoalescer";

const publicPropertyRecordCoalescer = createRequestCoalescer();
const activeRequest = { current: true };

class PublicPropertyRecord extends React.Component {  
  constructor(props) {
    super(props);

    this.state = {
      publicPropertyRecord: null,
    };

    this.mounted = { current: true };
    this.cancelLoadPublicPropertyRecord = () => {};
    this.createDiscrepancyField = this.createDiscrepancyField.bind(this);
    this.loadPublicPropertyRecord = this.loadPublicPropertyRecord.bind(this);
  }

  componentDidMount() {
    if (this.props.controlNumber) {
      this.loadPublicPropertyRecord();
    }
  }

  componentDidUpdate(prevProps) {
    const { controlNumber } = this.props;
    if (controlNumber && prevProps.controlNumber !== controlNumber) {
      this.setState({ publicPropertyRecord: null });
      this.loadPublicPropertyRecord();
    }
  }

  componentWillUnmount() {
    this.mounted.current = false;
    this.cancelLoadPublicPropertyRecord();
  }

  loadPublicPropertyRecord() {
    this.cancelLoadPublicPropertyRecord();
    this.cancelLoadPublicPropertyRecord = publicPropertyRecordCoalescer.run(
      this.props.controlNumber,
      (resolve, reject) => getPublicPropertyRecordRequest(this.props.controlNumber, resolve, activeRequest, reject),
      (publicPropertyRecord) => {
        if (this.mounted.current) {
          this.setState({ publicPropertyRecord });
        }
      },
      () => {
        if (this.mounted.current) {
          this.setState({ publicPropertyRecord: {} });
        }
      },
    );
  }

  createDiscrepancyField() {
    const publicPropertyRecord = this.state.publicPropertyRecord ?? {};
    const { buildingArea, discrepancyStatus, discrepancyExplanation, sketchSqFt } = publicPropertyRecord;

    if (buildingArea && buildingArea > 0) {
      switch (discrepancyStatus) {
        case "ACCEPTED":
          return {
            label: "Sketch Discrepancy",
            value: "No"
          };
        case "EXPLAINED":
          return {
            label: "Sketch Discrepancy",
            value: { label: "Yes", onClick: () => { } },
            dataType: DATA_TYPE_HYPERLINK,
            tooltip: {
              title: "Details", fields: [{
                label: "", value: `The Mueller Field Representative is aware that the Public Record information for this home indicates a TLA of ${buildingArea} SQFT.
After review of the home and the attached diagram the Mueller Field Representative believes the correct TLA is ${sketchSqFt} SQFT.      
The Mueller Field Representative identified the most likely reason(s) for the discrepancy as: ${discrepancyExplanation === "Unknown" ? "An error in the public record" : discrepancyExplanation}.`
              }]
            }
          };
        default:
          return {
            label: "Sketch Discrepancy",
            value: "Unconfirmed"
          };
      }
    }

    return {
      label: "Sketch Discrepancy"
    };
  }

  getAddressValue() {
    const publicPropertyRecord = this.state.publicPropertyRecord ?? {};
    const { siteAddress, siteCity, siteState, siteZip } = publicPropertyRecord;
    if (!siteAddress) {
      return null;
    } else {
      return { label: siteAddress, onClick: () => openGoogleMapsLink(`${siteAddress} ${siteCity}, ${siteState} ${siteZip}`)};
    }
  }

  render() {
    const publicPropertyRecord = this.state.publicPropertyRecord ?? {};
    const { siteAddress, siteCity, siteState, siteZip, yearBuilt, buildingArea, numberOfStories, numberOfUnits, bedrooms, baths, pdfPath, discrepancyEnabled } = publicPropertyRecord;

    let firstcolumn = [
      {
        label: "Address",
        value: this.getAddressValue(),
        dataType: siteAddress ? DATA_TYPE_HYPERLINK : null
      },
      {
        label: "City",
        value: siteCity
      },
      {
        label: "State",
        value: siteState
      },
      {
        label: "Zip",
        value: siteZip
      },
      {
        label: "Sketch",
        key: "pdfLink",
        value: { label: "Computer Generated", onClick: pdfPath },
        dataType: DATA_TYPE_HYPERLINK
      }
    ];

    if (discrepancyEnabled) {
      firstcolumn.push(this.createDiscrepancyField());
    }

    let secondcolumn = [
      {
        label: "Tax Record Year Built",
        value: yearBuilt
      },
      {
        label: "Total Living Area (sq ft)",
        value: buildingArea
      },
      {
        label: "# of Stories",
        value: numberOfStories
      },
      {
        label: "# of Units",
        value: numberOfUnits
      },
      {
        label: "# of Bedrooms",
        value: bedrooms
      },
      {
        label: "# of Baths",
        value: baths
      },
    ];

    return (
      <MCard title="Public Property Record" isFetching={this.state.publicPropertyRecord === null}
        fields={[firstcolumn, secondcolumn]} />
    );
  }
}
PublicPropertyRecord.propTypes = {
  controlNumber: PropTypes.number.isRequired,
};

export default PublicPropertyRecord;